// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase';
import { ArrowLeft, Mail, Send } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Please enter your email address'); return; }
    setLoading(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password',
      });
      if (resetError) throw resetError;
      setSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{
        background: '#fff', borderRadius: 20, padding: '40px 36px',
        width: '100%', maxWidth: 420,
        boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
        border: '1px solid #BBF7D0',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 52, marginBottom: 8 }}>{sent ? '✉️' : '🔐'}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#14532D' }}>
            {sent ? 'Check Your Email' : 'Reset Password'}
          </div>
          <div style={{ fontSize: 14, color: '#64748B', marginTop: 4 }}>
            {sent
              ? 'We sent a password reset link to your inbox'
              : 'Enter your email to receive a reset link'
            }
          </div>
        </div>

        {error && (
          <div style={{ background: '#FEE2E2', color: '#DC2626', fontSize: 13, padding: '10px 14px', borderRadius: 10, marginBottom: 16, textAlign: 'center' }}>
            {error}
          </div>
        )}

        {sent ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, marginBottom: 20 }}>
              We sent a password reset link to <strong style={{ color: '#14532D' }}>{email}</strong>.
              Click the link in the email to create a new password.
            </div>
            <div style={{
              fontSize: 12, color: '#64748B', marginBottom: 20,
              padding: 12, background: '#F0FDF4', borderRadius: 10,
              border: '1px solid #BBF7D0',
            }}>
              Didn't get it? Check your spam folder or wait a minute and try again.
            </div>
            <button
              onClick={() => { setSent(false); setEmail(''); }}
              className="btn btn-primary btn-lg"
              style={{ width: '100%', marginBottom: 12 }}
            >
              <Send size={18} /> Try Another Email
            </button>
            <Link to="/login" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 14, color: '#16A34A', fontWeight: 600, textDecoration: 'none', padding: '10px 0' }}>
              <ArrowLeft size={16} /> Back to Login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 22 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>Email Address</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </div>
            </div>
            <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={loading}>
              {loading ? 'Sending...' : <><Send size={18} /> Send Reset Link</>}
            </button>
            <Link to="/login" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 14, color: '#16A34A', fontWeight: 600, textDecoration: 'none', padding: '14px 0' }}>
              <ArrowLeft size={16} /> Back to Login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
